import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, RadioButton, RadioButtonGroup, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from './DataTable';
import { InlineError } from './states';
import { When } from './format';

// Đổi mã đại lý and Bỏ giữ chỗ (owner 2026-10-03): the founder role only; the API refuses every other role.
export function useIsFounder(): boolean {
  return useAuth().me?.role === 'founder';
}

// Đổi mã: new code plus a required choice for the old code (keep it reserved for this agent, or free it).
export function DoiMaDialog({ partner, open, onClose }: { partner: { id: string; code: string }; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [keep, setKeep] = useState<'' | 'giu' | 'tra'>('');
  const m = useMutation({
    mutationFn: () => api.patch('/api/admin/agents/' + partner.id, { code, keepOld: keep === 'giu' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent', partner.id] });
      qc.invalidateQueries({ queryKey: ['agents'] });
      qc.invalidateQueries({ queryKey: ['agent-reserved-codes'] });
      close();
    },
  });
  const close = () => { setCode(''); setKeep(''); m.reset(); onClose(); };
  const ok = /^\d{3,12}$/.test(code) && code !== partner.code && keep !== '';
  const cu = partner.code;
  return (
    <Modal open={open} modalHeading={`Đổi mã đại lý ${cu}`} primaryButtonText="Đổi mã" secondaryButtonText="Hủy"
      primaryButtonDisabled={!ok || m.isPending} onRequestClose={close} onRequestSubmit={() => { if (ok) m.mutate(); }}>
      <div className="stack">
        <TextInput id="ag-code" labelText="Mã mới (3 đến 12 chữ số)" value={code} maxLength={12}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); m.reset(); }} />
        <p>Thành viên, số dư và hoa hồng vẫn ở đại lý này: chúng gắn với đại lý, không gắn với mã.</p>
        <p><strong>Link và mã giới thiệu đi theo mã, không đi theo đại lý.</strong> Link cũ của đại lý mang mã {cu}.</p>
        <RadioButtonGroup name="ag-ma-cu" legendText={`Mã cũ ${cu}`} orientation="vertical" valueSelected={keep}
          onChange={(v) => { setKeep(v as 'giu' | 'tra'); m.reset(); }}>
          <RadioButton id="ag-ma-cu-giu" value="giu" labelText={`Giữ chỗ mã ${cu} cho đại lý này`} />
          <RadioButton id="ag-ma-cu-tra" value="tra" labelText={`Trả mã ${cu} lại để cấp cho đại lý khác`} />
        </RadioButtonGroup>
        {keep === 'giu' ? (
          <p className="muted">Người đăng ký bằng mã {cu} hoặc link cũ vẫn vào đại lý này. Không ai khác lấy được mã {cu} cho tới khi bỏ giữ chỗ ở trang Đại lý, mục Mã đang giữ chỗ.</p>
        ) : keep === 'tra' ? (
          <p className="muted">Từ lúc đổi, người đăng ký bằng mã {cu} hoặc link cũ không vào đại lý này nữa: họ vào đại lý nhận mã {cu} sau này, chưa ai nhận thì vào nhà cái. Người đã đăng ký trước vẫn ở đại lý này.</p>
        ) : null}
        <p className="muted">Đại lý phải đăng nhập lại portal và gửi link mới{code ? ` (mã ${code})` : ''} cho khách.</p>
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

type Reserved = {
  code: string; reason: string; createdAt: string;
  partner: { id: string; code: string; displayName: string; isSuperRoot: boolean; status: string } | null;
};

const LY_DO: Record<string, string> = { 'house-rename': 'Mã cũ của nhà cái', 'agent-rename': 'Mã cũ khi đổi mã đại lý' };
const giuCho = (r: Reserved) => (!r.partner ? 'Không rõ' : r.partner.isSuperRoot ? 'Nhà cái' : `Đại lý ${r.partner.code} · ${r.partner.displayName}`);

// Mã đang giữ chỗ: codes nobody may take (old house codes, an agent's old code kept at Đổi mã), with Bỏ giữ chỗ.
export function MaGiuCho() {
  const qc = useQueryClient();
  const founder = useIsFounder();
  const [pick, setPick] = useState<Reserved | null>(null);
  const q = useQuery({ queryKey: ['agent-reserved-codes'], queryFn: () => api.get<{ items: Reserved[] }>('/api/admin/agents/reserved-codes') });
  const m = useMutation({
    mutationFn: (code: string) => api.del('/api/admin/agents/reserved-codes/' + code),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agent-reserved-codes'] }); setPick(null); },
  });
  const columns: ColumnDef<Reserved, any>[] = [
    { header: 'Mã', accessorKey: 'code', cell: (c) => <span className="num">{c.getValue()}</span> },
    { header: 'Giữ cho', id: 'cho', cell: (c) => {
      const p = c.row.original.partner;
      return p ? <Link to={'/agents/' + p.id}>{giuCho(c.row.original)}</Link> : giuCho(c.row.original);
    } },
    { header: 'Lý do', accessorKey: 'reason', cell: (c) => LY_DO[c.getValue()] ?? c.getValue() },
    { header: 'Từ lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
    ...(founder ? [{ header: '', id: 'bo', cell: (c: { row: { original: Reserved } }) => (
      <Button size="sm" kind="danger--tertiary" onClick={() => { m.reset(); setPick(c.row.original); }}>Bỏ giữ chỗ</Button>
    ) } as ColumnDef<Reserved, any>] : []),
  ];
  return (
    <section className="section">
      <h2>Mã đang giữ chỗ</h2>
      <p className="muted">Không ai lấy được các mã này. Người đăng ký bằng một mã đang giữ chỗ hoặc link mang mã đó vào nơi mã được giữ cho (mã cũ của nhà cái: vào nhà cái).</p>
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()} rowKey={(r) => r.code}
        empty={{ title: 'Không có mã nào đang giữ chỗ' }} />
      <Modal open={!!pick} danger modalHeading={`Bỏ giữ chỗ mã ${pick?.code ?? ''}?`} primaryButtonText="Bỏ giữ chỗ" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setPick(null)} onRequestSubmit={() => { if (pick) m.mutate(pick.code); }}>
        {pick ? (
          <div className="stack">
            <p>Mã {pick.code} đang giữ cho {giuCho(pick)}. Sau khi bỏ giữ chỗ, mã này cấp được cho đại lý mới (Kích đại lý hoặc Đổi mã).</p>
            <p><strong>Link và mã giới thiệu đi theo mã:</strong> từ lúc này, người đăng ký bằng mã {pick.code} hoặc link mang mã đó vào đại lý nhận mã {pick.code}; chưa ai nhận thì vào nhà cái. Người đã đăng ký trước vẫn ở chỗ cũ.</p>
            <InlineError error={m.error} />
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
